import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { createEntityAdapter } from '@reduxjs/toolkit'
import './DataSets.css'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import ComponentBox from '../../components/ComponentBox/ComponentBox'
import { useNavigate } from 'react-router-dom'
import ICSDataGrid from '../../components/ICSDataGrid/ICSDataGrid'
import { getAllDatasets, removeDataset } from '../../redux/slices'
import { DefaultState } from '../../types'
import { Container, Col, Row, ModalBody } from 'reactstrap'
import HeaderBar from '../../components/HeaderBar/HeaderBar'
import ICSActionButton from '../../components/ICS/ActionButton/ICSActionButton'
import { ICSDataCellDetailView } from '../../components/ICSDataCellDetailView/ICSDataCellDetailView'
import ICSErrorCard from '../../components/ICSErrorCard/ICSErrorCard'
import { AppDispatch } from '../../services/lib'
import { ICSModal } from '../../components'
import DataSetReview from './DataSetReview/DataSetReview'
const helpText = (
  <div>
    <p>Data Sets define the source data passed to your Analyzer.</p>
    <p>
      Data Sets can be generated either by selecting criteria, or by selecting
      specific files from the data query.
    </p>
    <p>
      <strong>Files</strong>
    </p>
    <p>
      Simply navigate to Generate page, perform a search, select your desired
      files and click "Create DataSet".
    </p>
    <p>
      This will generate a Static Data Set which can be used for on demand
      Analysis.
    </p>

    <p>
      <strong>Criteria</strong>
    </p>
    <p>
      A dynamic Data Set is a collection of criteria in any combination or
      number of:
    </p>
    <ul>
      <li>Fleets and/or Vehicles</li>
      <li>Collections and/or file names</li>
      <li>File extensions</li>
      <li>Dates</li>
      <li>Scripts</li>
    </ul>
    <p>
      These Data Sets must be either Static, which are used for on demand
      analysis on existing files, or Dynamic, which are used for ongoing
      analysis of files as they are collected
    </p>

    <p>
      You may assign multiple Data Sets to an Analyzer so there is no need to
      create or maintain a single all-inclusive Data Set.
    </p>
    <p>
      Data Sets created by you are only available to you so may add, edit, or
      discard them at any time.
    </p>
  </div>
)



const DataSetList = (): JSX.Element => {
  const navigate = useNavigate()
  const selectors = createEntityAdapter().getSelectors()
  const [fileModalVisible, setFileModalVisible] = useState<boolean>(false)
  const [selectedDS, setSelectedDS] = useState<string>('')
  const datasetsState = useSelector((state: DefaultState) => state.datasets)
  const datasetErrors = useSelector(
    (state: DefaultState) => state.datasets.error
  )
  const isAdmin: boolean | undefined = useSelector(
    (state: DefaultState) => state.session.data.user?.roles?.includes('admin')
  )
  const datasetsArray = selectors.selectAll(datasetsState)

  const dispatch: AppDispatch = useDispatch()

  useEffect(() => {
    const initialState = async () => {
      await dispatch(getAllDatasets())
    }
    initialState()
  }, [dispatch])

  const datasetsAddButtonClicked = () => {
    navigate('/ui2/datasets/generate/')
  }

  const generateAdminButtons = (dispatch: AppDispatch) => {
    return [
      {
        hint: 'Delete',
        icon: 'trash',
        onClick: async function review(e) {
          const r = window.confirm(
            `Are you sure you want to delete the following Dataset: ${e.row.data.name}?`
          )
          if (r === true) {
            await dispatch(removeDataset(e.row.data.id))
            await dispatch(getAllDatasets())
          }
        },
      },
      {
        hint: 'View',
        icon: 'find',
        onClick: async function review(e) {
          setSelectedDS(e.row.data.id)
          setFileModalVisible(true)
        },
      },
    ].filter(button => isAdmin || button.hint !== 'Delete')
  }

  const adminButtons = generateAdminButtons(dispatch)

  return (
    <>
      <Container fluid={true}>
        <Row>
          <Col className='wivi-col'>
            <HeaderBar
              origin='IPA'
              routes={[
                {
                  title: 'Data Sets',
                },
              ]}
              help={{ title: 'Data Sets', text: helpText }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={{ size: 12 }} className='wivi-col'>
            <ComponentBox>
              <ICSModal
                visible={fileModalVisible}
                onHiding={() => {
                  setFileModalVisible(false)
                }}
                title='Dataset Files'
                defaultWidth='80vw'
                defaultHeight='50vh'
                resizeEnabled={true}
              >
                <ModalBody>
                  {fileModalVisible && <DataSetReview datasetId={selectedDS} />}
                </ModalBody>
              </ICSModal>
              {datasetErrors?.code === 'error' ? (
                <ICSErrorCard
                  code={datasetErrors.code}
                  message={datasetErrors.message}
                  fields={datasetErrors.fields}
                />
              ) : (
                <ICSDataGrid
                  dataSource={datasetsArray}
                  columnOptions={[
                    {
                      type: 'buttons',
                      caption: 'Action',
                      buttons: adminButtons,
                      // buttons: /*isAdmin*/ true
                      //   ? adminButtons
                      //   : adminButtons.splice(0, 2),
                    },
                    { dataField: 'name', caption: 'Name' },
                    {
                      dataField: 'type',
                      caption: 'Type',
                      width: '70px',
                    },
                    {
                      dataField: 'description',
                      caption: 'Description',
                      cssClass: 'td-dataset-description',
                      cellRender: data => {
                        return (
                          <ICSDataCellDetailView
                            {...data}
                            modalWidth='35vw'
                            modalHeight='20vh'
                          />
                        )
                      },
                    },
                    {
                      dataField: 'user.name',
                      caption: 'User',
                      width: '15vw',
                    },
                    {
                      dataField: 'created_at',
                      caption: 'Create Date',
                      width: '15vw',
                    },
                  ]}
                  options={{
                    buttons: [
                      <ICSActionButton
                        id='datasetAddButton'
                        type='success'
                        width={65}
                        height={35}
                        stylingMode='contained'
                        onClick={datasetsAddButtonClicked}
                        text='Add'
                      >
                        <span className='glyphicons glyphicons-floppy-disk'></span>
                        <span className='glyphicons glyphicons-plus'></span>
                      </ICSActionButton>,
                    ],
                  }}
                />
              )}
            </ComponentBox>
          </Col>
        </Row>
      </Container>
    </>
  )
}
export default DataSetList
